export const EULA_NANO = `
<div class="row">
    <div class="col-12 text-center">
        <h3>AI-Generated Content License Agreement</h3>
    </div>
</div>

<h6>1. Definitions</h6>
<ul>
    <li>1.1 “AI-Generated Content / AI Image” means any visual content generated using IndiaPicture’s AI tools, including content created through third-party AI engines integrated into the platform.</li>
    <li>1.2 “Unreviewed AI Image” means an AI image downloaded immediately after generation, without undergoing IndiaPicture’s compliance review. These images are permitted only for internal evaluation and are not cleared for external or commercial use.</li>
    <li>1.3 “Reviewed / Approved AI Image” means an AI image that has passed IndiaPicture’s compliance review, is approved for commercial use, and is issued with a Commercial AI License Certificate.</li>
    <li>1.4 “Synthetic Human” means an AI-generated person created by the system who may unintentionally resemble a real individual.</li>
    <li>1.5 “User Input” means all prompts, text, uploads, references, and any material submitted by the user to generate AI outputs.</li>
    <li>1.6 “Commercial Use” means any public-facing, monetized, promotional, published, or client-facing use, including ads, marketing, print, digital, packaging, editorial, social media, apps, websites, and broadcast.</li>
</ul>

<h6>2. License Framework for AI-Generated Content</h6>

<h6>2.1 Unreviewed AI Images — Internal Use Only</h6>
<p>Unreviewed AI images may be used strictly for:</p>
<ul>
    <li>Internal drafts, exploration, and creative development</li>
    <li>Internal team review</li>
    <li>Mood boards or concept ideation</li>
    <li>Non-public, non-client, non-distributed internal use</li>
</ul>

<p>Unreviewed images may NOT be used for:</p>
<ul>
    <li>Advertising, marketing, branding, packaging</li>
    <li>Social media, websites, apps, or digital publishing</li>
    <li>Editorial, print, outdoor, or broadcast</li>
    <li>Client presentations, pitches, proposals, activation decks</li>
    <li>Sharing with clients, agencies, freelancers, vendors, or partners</li>
    <li>WhatsApp, email, cloud sharing, or public posting</li>
</ul>

<p>Unreviewed images include:</p>
<ul>
    <li>No commercial rights</li>
    <li>No legal clearance or warranties</li>
    <li>No indemnification</li>
</ul>

<h6>2.2 Reviewed & Approved AI Images — Commercial License</h6>
<ul>
    <li>AI images approved through IndiaPicture’s compliance review are cleared for commercial usage.</li>
    <li>Approved images may be used across digital, print, broadcast, OOH, packaging, websites, social media, apps, and promotional materials.</li>
    <li>Approved images are accompanied by a Commercial AI License Certificate.</li>
    <li>Approved images may be eligible for indemnification only as provided under the integrated provider’s standard API licensing for licensed generated assets.</li>
    <li>Approved images may be provided in upscaled resolution (when available).</li>
</ul>

<p>Rejected images remain internal-use only.</p>

<h6>3. Non-Exclusivity & Non-Uniqueness</h6>
<ul>
    <li>AI images are non-exclusive and may resemble outputs generated by other users.</li>
    <li>IndiaPicture does not guarantee originality or uniqueness.</li>
    <li>IndiaPicture is not responsible for conceptual similarity across users due to shared AI models.</li>
</ul>

<h6>4. Prohibited AI Uses</h6>

<h6>4.1 Identity & Personal Rights</h6>
<ul>
    <li>Public figures, celebrities, ministers, influencers, or recognizable individuals</li>
    <li>Deepfake-style outputs</li>
    <li>Synthetic humans resembling real persons</li>
    <li>Attempts to recreate real people from descriptions or uploaded references</li>
</ul>

<h6>4.2 Copyright & Trademarks</h6>
<ul>
    <li>Logos, brand names, trademarks, copyrighted characters</li>
    <li>Known copyrighted artistic styles</li>
    <li>Uploading copyrighted material without permission</li>
    <li>Attempts to replicate branded products</li>
</ul>

<h6>4.3 Sensitive Content</h6>
<ul>
    <li>Adult, explicit, or sexual content</li>
    <li>Violence, hate, discrimination, or harassment</li>
    <li>Political campaigns or ideologically sensitive material</li>
    <li>Religious disrespect, extremist content, or harmful propaganda</li>
    <li>Minors in sensitive or dangerous contexts</li>
    <li>Medical, legal, financial, or regulated-industry claims</li>
</ul>

<p>IndiaPicture may block or penalize any attempt to generate restricted content.</p>

<h6>5. Modification Restrictions</h6>
<ul>
    <li>Editing or modifying AI images does not expand or change the licensed rights.</li>
    <li>Risky or misleading modifications void any indemnification (if available under provider terms).</li>
    <li>Significant edits may require resubmission for compliance review.</li>
</ul>

<h6>6. Sharing & Distribution Restrictions</h6>
<p>Users may NOT share Unreviewed AI Images:</p>
<ul>
    <li>With clients or external stakeholders</li>
    <li>In presentations, proposals, or published materials</li>
    <li>On social media, websites, or public platforms</li>
    <li>With freelancers, vendors, third-party agencies</li>
    <li>Via WhatsApp, email, or cloud links</li>
</ul>

<p>Sharing unreviewed images externally is treated as public use and is strictly prohibited.</p>

<h6>7. AI Training Restrictions</h6>
<ul>
    <li>Users may NOT use IndiaPicture AI outputs to train or improve AI or machine-learning systems.</li>
    <li>Users may NOT build datasets using AI outputs.</li>
    <li>Users may NOT resell or redistribute IndiaPicture AI outputs for training purposes.</li>
</ul>

<p>This restriction is absolute and non-negotiable.</p>

<h6>8. Compliance Review Process</h6>

<h6>8.1 Submission</h6>
<ul>
    <li>User submits selected images for review.</li>
</ul>

<h6>8.2 Evaluation</h6>
<p>IndiaPicture reviews for:</p>
<ul>
    <li>Resemblance to real individuals</li>
    <li>Copyright or trademark conflicts</li>
    <li>Sensitive or risky content</li>
    <li>AI distortions or biases</li>
    <li>General compliance and legal safety</li>
</ul>

<h6>8.3 Possible Outcomes</h6>
<ul>
    <li>Approved → Commercial AI License Certificate issued</li>
    <li>Rejected → Internal-use only</li>
    <li>Returned → Requires modifications before resubmission</li>
</ul>

<h6>8.4 Disclaimer</h6>
<p>Approval signifies a reasonable compliance check, not a guarantee of zero legal risk.</p>

<h6>9. User Warranties</h6>
<ul>
    <li>Users confirm that prompts and uploads are legally owned or licensed.</li>
    <li>Users will not upload protected or copyrighted materials.</li>
    <li>Users comply with all applicable laws.</li>
    <li>Users indemnify IndiaPicture against losses arising from violations.</li>
</ul>

<h6>10. Accuracy, Bias & Representation Disclaimer</h6>
<p>AI outputs may:</p>
<ul>
    <li>Contain errors or distortions</li>
    <li>Reflect algorithmic biases</li>
    <li>Misrepresent human features</li>
    <li>Produce unrealistic or unexpected results</li>
</ul>
<p>User is responsible for verifying suitability.</p>

<h6>11. Indemnification</h6>

<h6>11.1 No Indemnification for Unreviewed / Preview Outputs</h6>
<ul>
    <li>Unreviewed AI images include no indemnification.</li>
</ul>

<h6>11.2 Licensed Generated Assets (If Applicable)</h6>
<p>Where applicable, indemnification is governed solely by the provider’s API licensing terms.</p>

<h6>11.3 No IndiaPicture Indemnity Amount / Cap</h6>
<ul>
    <li>IndiaPicture does not provide independent indemnification.</li>
</ul>

<h6>11.4 Indemnification Exclusions</h6>
<p>Indemnification (if applicable) does not apply to:</p>
<ul>
    <li>Unreviewed / preview images</li>
    <li>Prohibited uses or prompts</li>
    <li>Uploaded copyrighted content</li>
    <li>Misleading or defamatory uses</li>
    <li>Material modifications</li>
    <li>Any unlawful usage</li>
</ul>

<h6>12. Limitations of Liability</h6>
<ul>
    <li>IndiaPicture is not liable for misuse of AI outputs.</li>
    <li>Not liable for resemblance to real individuals.</li>
    <li>Not liable for damages from user prompts, uploads, or errors.</li>
    <li>Not liable for platform downtime or generation failures.</li>
</ul>

<h6>13. Termination of License</h6>
<ul>
    <li>IndiaPicture may revoke licensing if the user attempts prohibited content generation.</li>
    <li>License may be revoked for sharing unreviewed images externally.</li>
    <li>Uploading illegal or copyrighted materials may result in termination.</li>
    <li>Attempts to bypass compliance review may result in restrictions.</li>
    <li>Using IndiaPicture outputs to train external AI models is grounds for termination.</li>
    <li>Repeated violations may lead to permanent access restriction.</li>
</ul>

<p>Termination may occur without prior notice in high-risk cases.</p>
`;
